import { useMemo, useState } from 'react';
import { AddIndexTable } from '@/widgets/IndexWidgets/AddIndexTable';

import { IndexDTO } from '@/types/api-types';

import IconMobileLogo from '@/assets/svg/icon-add-mobile.svg?react';

import { useIsMobile } from '@/hooks/use-mobile';

import { Button } from '@/components/ui/button';
import { AddIndexDialog } from '@/components/AddIndexDialog';
import { ConfirmationDialog } from '@/components/ConfirmationDialog/ConfirmationDialog';

import { useGlobalStore } from '@/stores/globalStore';

export function AddIndexTab() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<IndexDTO | null>(null);
  const [indexToDelete, setIndexToDelete] = useState<string | null>(null);
  const { indexes, createIndex, updateIndex, deleteIndex } = useGlobalStore();
  const [isLoading, setIsLoading] = useState(false);

  const isMobile = useIsMobile();

  const visibleIndexes = useMemo(() => {
    return indexes.filter((index) => !index.is_deleted);
  }, [indexes]);

  const handleDelete = async (id: string) => {
    await deleteIndex(id);
    setIndexToDelete(null);
  };

  const handleAddIndex = async (index: IndexDTO) => {
    setIsLoading(true);
    setDialogOpen(false);
    try {
      await createIndex(index);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditIndex = async (index: IndexDTO) => {
    setIsLoading(true);
    try {
      await updateIndex(index.uuid, index);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ConfirmationDialog
        isOpen={!!indexToDelete}
        onClose={() => setIndexToDelete(null)}
        onConfirm={() => handleDelete(indexToDelete!)}
        title="Видалити індекс?"
        cancelText="Скасувати"
        confirmText="Видалити"
      />
      <div className="flex h-full flex-col rounded-md bg-white shadow-sm">
        <div className="flex items-center justify-between border-b pb-2 pl-8 pr-[22px] pt-4">
          <h2 className="text-2xl font-medium text-primary">Список індексів</h2>
          {isMobile ? (
            <button
              className="rounded-md text-white"
              onClick={() => setDialogOpen(true)}
            >
              <IconMobileLogo className="h-[34px] w-[34px] fill-primary" />
            </button>
          ) : (
            <Button
              className="h-[50px] w-[150px] rounded-md bg-primary text-white"
              onClick={() => setDialogOpen(true)}
            >
              Додати індекс
            </Button>
          )}
        </div>

        <div className="flex-grow overflow-auto">
          <AddIndexTable
            data={visibleIndexes}
            onEdit={(index) => {
              setEditingIndex(index);
              setDialogOpen(true);
            }}
            onDelete={(id) => setIndexToDelete(id)}
            isLoading={isLoading}
          />
        </div>

        <AddIndexDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          editingIndex={editingIndex}
          onClose={() => setEditingIndex(null)}
          onAdd={handleAddIndex}
          onEdit={handleEditIndex}
        />
      </div>
    </>
  );
}
